// @flow

import { Operation } from 'apollo-link';
import { has } from 'lodash';

import ActionTypes from 'app/common/apollo/ActionTypes';

// What?
//
// The `ApolloReduxLink` will `dispatch` upon completion of all Apollo
// query/mutation operations. Reducers may update state based on this.
//
// Rather than leave repetitive per-`operationName` plumbing to each
// reducer, a `linkStateSync` can be created given an object containing
// per-`operationName` functions which take current `state` + the
// response from the Apollo request.

type Handler = (state: Object, action: Object) => Object;

type StateLinkSyncHandlers = { [string]: Handler };

type OperationResult = { data?: Object, errors?: string[] };

type StateLinkSyncAction = {
  type: string,
  operationName: string,
  data?: Object,
  result: {},
};

const selectOperationHandler = (handlers, operationName: string) =>
  handlers[operationName];

const selectOperationData = (action: StateLinkSyncAction): ?Object =>
  action.data && action.operationName
    ? action.data[action.operationName]
    : null;

export const createReduxAction = (
  operation: Operation,
  result: OperationResult
): StateLinkSyncAction => ({
  type: ActionTypes.APOLLO_LINK_STATE_SYNC,
  operationName: operation.operationName,
  data: result.data,
  result,
});

export const createLinkStateSync = (handlers: StateLinkSyncHandlers) => (
  state: Object,
  action: StateLinkSyncAction
) => {
  const { operationName } = action;
  if (has(handlers, operationName)) {
    const handler = selectOperationHandler(handlers, operationName);
    const data = selectOperationData(action);
    return handler(state, data);
  }
  return state;
};
